<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, inject, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useEventBus } from "@vueuse/core"
import { useAuthStore } from "@core/stores/auth.store"
import { storeToRefs } from "pinia"
import {
    formatPhone,
    phoneValidator,
    formatAddress,
    formatDate,
} from "@core/helpers"
import type { Services } from "@services"
import type { CompanyGeneralInfo } from "@modules/espace-entreprise/types/company"
import { useFetchWrapper } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Informations générales",
            representant: "Représentant",
            date: "Date de création",
            idcompany: {
                entreprise: "SIREN",
                asso: "RNA",
                copro: "Numéro de copropriété",
            },
            legal_form: "Forme juridique",
            phone: {
                label: "Numéro de téléphone",
                placeholder: "Ex : 06 12 34 56 78",
                hint: "Format attendu : 06 12 34 56 78 ou +33 6 12 34 56 78",
                error: "Le format du numéro de téléphone est invalide",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Field = {
    label: string
    value: string
}

// ================================
// SERVICES & STORES
// ================================
const services = inject<Services>("services")!
const authStore = useAuthStore()
const {
    companyId,
    companyName,
    companyIdcompany,
    companyLegalForm,
    companyAddress,
    companyType,
    companyRepresentant,
    companyCreatedAt,
    companyPhone,
    companyImg,
} = storeToRefs(authStore)

/**
 * Type pour le service de mise à jour de l'entreprise
 * Note: Le type sera généré automatiquement après la régénération des types de services
 */
type UpdateCompanyPayload = {
    phone?: string
    address?: unknown
    representant?: string
    idcompany?: string
    legalForm?: string
}

type UpdateCompanyService = (
    payload: UpdateCompanyPayload
) => Promise<{ success: boolean; phone?: string }>

/**
 * Bus d'événements pour les notifications
 */
const busNotification = useEventBus<{
    severity: "warn" | "error" | "success" | "info"
    title: string
    message: string
    life?: number
}>("notification")

// ================================
// STATE
// ================================

/**
 * Formate un numéro de téléphone en supprimant les espaces
 * @param phoneValue - Numéro de téléphone à formater
 * @returns Numéro de téléphone sans espaces
 */
function formatPhoneValue(phoneValue?: string | null): string {
    return phoneValue?.replace(/ /g, "") ?? ""
}

/**
 * Numéro de téléphone de l'organisation (sans espaces), éditable localement
 */
const phone = ref<string>("")

/**
 * Indique si le champ de modification du numéro de téléphone est ouvert
 */
const isUpdatePhone = ref<boolean>(false)

/**
 * Indique si la modal d'image est ouverte
 */
const isImageModalOpen = ref<boolean>(false)

// ================================
// COMPUTED
// ================================

/**
 * Informations de l'entreprise depuis le store auth (format CompanyGeneralInfo)
 */
const getCompanyInfo = computed<CompanyGeneralInfo | null>(() => {
    if (!companyId.value) return null
    const type: "entreprise" | "asso" | "copro" | "collectivite" =
        companyType.value || "entreprise"
    return {
        _id: companyId.value,
        name: companyName.value,
        idcompany: companyIdcompany.value ?? "",
        legalForm: companyLegalForm.value,
        address: (companyAddress.value ?? {}) as CompanyGeneralInfo["address"],
        type,
        representant: companyRepresentant.value,
        createdAt: companyCreatedAt.value,
        phone: companyPhone.value,
        img: companyImg.value,
        operation: {
            stripe: { plan: "", subscription_active: false },
            beta_features: {},
        },
    } as CompanyGeneralInfo
})

/**
 * Récupère la liste des champs à afficher selon le type d'organisation
 */
const getFields = computed<Field[]>(() => {
    const company = getCompanyInfo.value
    if (!company) return []
    const fields: Field[] = []

    // Ajoute le numéro d'immatriculation si l'organisation n'est pas une collectivité
    if (company.type !== "collectivite") {
        fields.push({
            label: t(`idcompany.${company.type}`),
            value: company.idcompany ?? "-",
        })
    }

    // Ajoute la forme juridique pour les entreprises
    if (company.type === "entreprise") {
        fields.push({
            label: t("legal_form"),
            value: company.legalForm ?? "-",
        })
    }

    // Information sur le représentant de l'organisation
    fields.push({
        label: t("representant"),
        value: company.representant ?? "-",
    })

    // Information sur la date de création de l'organisation chez Homeys
    fields.push({
        label: t("date"),
        value: company.createdAt
            ? formatDate(company.createdAt, "DD/MM/YYYY", "-")
            : "-",
    })

    return fields
})

// Synchronise le ref phone avec l'entreprise du store (à l'init et après fetchUser)
watch(
    getCompanyInfo,
    (info) => {
        phone.value = formatPhoneValue(info?.phone)
    },
    { immediate: true }
)

/**
 * Indique si l'utilisateur est administrateur de l'entreprise
 */
const getIsCompanyAdmin = computed<boolean>(() => {
    return authStore.isCompanyAdmin
})

// ================================
// BUSINESS LOGIC (mise à jour téléphone)
// ================================

/**
 * Met à jour le numéro de téléphone de l'organisation
 * @param newPhoneValue - Nouvelle valeur du numéro de téléphone
 */
const { fetch: onUpdatePhone, isFetching: isUpdatingPhone } = useFetchWrapper(
    async (newPhoneValue: string) => {
        // Garde la valeur précédente en cas d'erreur
        const previousValue = phone.value

        try {
            // Supprime les espaces dans le numéro de téléphone
            const newPhoneValueFormatted = newPhoneValue?.replace(/ /g, "")

            // Met à jour le numéro de téléphone via le service
            // Note: Le type sera généré automatiquement après la régénération des types de services
            const updateCompany = (
                services as Services & {
                    $updateCompany: UpdateCompanyService
                }
            ).$updateCompany
            await updateCompany({
                phone: newPhoneValueFormatted,
            })

            // Met à jour la valeur locale
            phone.value = newPhoneValueFormatted

            // Rafraîchit les données utilisateur
            await authStore.fetchUser()

            // Affiche une notification de succès
            busNotification.emit({
                severity: "success",
                title: "Succès",
                message: "Le numéro de téléphone a été mis à jour avec succès",
            })

            // Ferme le mode édition
            isUpdatePhone.value = false
        } catch (err: unknown) {
            // Si une erreur survient, on remet la valeur précédente
            phone.value = previousValue

            // Affiche une notification d'erreur
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : "Une erreur est survenue lors de la mise à jour du numéro de téléphone"
            busNotification.emit({
                severity: "error",
                title: "Erreur",
                message: errorMessage,
            })
        }
    }
)

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le changement de valeur du numéro de téléphone
 * @param newPhoneValue - Nouvelle valeur du numéro de téléphone
 */
function onChangePhoneValue(newPhoneValue: string): void {
    onUpdatePhone(newPhoneValue)
}

/**
 * Annule la modification du téléphone et restaure la valeur initiale
 */
function onCancelPhoneEdit(): void {
    phone.value = formatPhoneValue(getCompanyInfo.value?.phone)
    isUpdatePhone.value = false
}

/**
 * Ouvre la modal d'édition de l'image de l'entreprise
 */
function onClickImage(): void {
    if (getIsCompanyAdmin.value) {
        isImageModalOpen.value = true
    }
}
</script>

<template>
    <BaseTitledCard v-if="getCompanyInfo" class="company-general-info">
        <template #header>
            {{ t("title") }}
        </template>
        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template #content>
            <div class="w-full">
                <!-- ================================ -->
                <!-- MODAL D'IMAGE -->
                <!-- ================================ -->
                <ModalCompanyImage
                    v-model:open="isImageModalOpen"
                    :crop-image="getCompanyInfo?.img?.crop_image"
                    :raw-image="getCompanyInfo?.img?.raw_image"
                    :company-id="getCompanyInfo?._id"
                />

                <!-- ================================ -->
                <!-- BLOC AVATAR, ADRESSE ET TÉLÉPHONE -->
                <!-- ================================ -->
                <div class="flex flex-col items-center gap-6 py-6">
                    <!-- Avatar avec image -->
                    <div
                        class="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer transition-all duration-300"
                        :class="{
                            'bg-blue-100': !getCompanyInfo?.img?.crop_image,
                            'cursor-pointer hover:opacity-75':
                                getIsCompanyAdmin &&
                                getCompanyInfo?.img?.crop_image,
                            'cursor-default': !getIsCompanyAdmin,
                        }"
                        @click="onClickImage"
                    >
                        <img
                            v-if="getCompanyInfo?.img?.crop_image"
                            :src="getCompanyInfo.img.crop_image"
                            class="w-full h-full object-cover transition-opacity duration-300 relative z-20"
                            :class="{
                                'hover:opacity-50': getIsCompanyAdmin,
                            }"
                            alt="Logo de l'entreprise"
                        />
                        <BaseIcon
                            v-else
                            icon="pi pi-image"
                            size="32px"
                            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 transition-opacity duration-300 pointer-events-none"
                            :class="{
                                'opacity-0':
                                    getCompanyInfo?.img?.crop_image &&
                                    !getIsCompanyAdmin,
                                'opacity-100':
                                    !getCompanyInfo?.img?.crop_image ||
                                    (getIsCompanyAdmin &&
                                        !getCompanyInfo?.img?.crop_image),
                            }"
                            :color="
                                getIsCompanyAdmin &&
                                getCompanyInfo?.img?.crop_image
                                    ? '#60a5fa'
                                    : '#9ca3af'
                            "
                        />
                    </div>

                    <!-- Informations de l'entreprise -->
                    <div
                        class="flex flex-col items-center gap-4 max-w-md w-full"
                    >
                        <h3 class="text-xl font-semibold text-blue-700">
                            {{ getCompanyInfo.name }}
                        </h3>
                        <p class="text-base text-gray-700 text-center">
                            {{ formatAddress(getCompanyInfo.address) }}
                        </p>

                        <!-- Champ téléphone éditable (Inplace) -->
                        <BaseInplace
                            v-model:active="isUpdatePhone"
                            :disabled="!getIsCompanyAdmin"
                        >
                            <template #display>
                                <div
                                    class="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded p-4 transition-colors"
                                >
                                    <p class="text-base text-gray-700">
                                        {{ formatPhone(phone) || "-" }}
                                    </p>
                                    <BaseIcon
                                        v-if="getIsCompanyAdmin"
                                        icon="pi pi-pencil"
                                        size="16px"
                                        class="ml-2"
                                    />
                                </div>
                            </template>
                            <template #content>
                                <div class="flex flex-col gap-2">
                                    <BaseInputWrapper
                                        input-id="company-phone"
                                        :hint="t('phone.hint')"
                                        :error="
                                            phone && !phoneValidator(phone)
                                                ? t('phone.error')
                                                : ''
                                        "
                                    >
                                        <BaseInputText
                                            id="company-phone"
                                            v-model="phone"
                                            type="tel"
                                            :placeholder="
                                                t('phone.placeholder')
                                            "
                                            :invalid="
                                                !!(
                                                    phone &&
                                                    !phoneValidator(phone)
                                                )
                                            "
                                            class="w-full"
                                            autofocus
                                            @keyup.enter="
                                                phoneValidator(phone) &&
                                                onChangePhoneValue(phone)
                                            "
                                        />
                                    </BaseInputWrapper>
                                    <div class="flex gap-2 justify-end">
                                        <BaseButton
                                            severity="secondary"
                                            size="small"
                                            label="Annuler"
                                            @click="onCancelPhoneEdit()"
                                        />
                                        <BaseButton
                                            size="small"
                                            label="Valider"
                                            :disabled="
                                                !phoneValidator(phone) ||
                                                isUpdatingPhone
                                            "
                                            @click="onChangePhoneValue(phone)"
                                        />
                                    </div>
                                </div>
                            </template>
                        </BaseInplace>
                    </div>
                </div>

                <!-- ================================ -->
                <!-- BLOC DES CHAMPS D'INFORMATION -->
                <!-- ================================ -->
                <div
                    class="md:grid md:gap-16 border-t-1 border-gray-200 py-6 px-8 md:px-14 flex flex-col gap-2"
                    :class="{
                        'grid-cols-1': getFields.length <= 1,
                        'grid-cols-2': getFields.length === 2,
                        'grid-cols-3': getFields.length === 3,
                        'grid-cols-4': getFields.length >= 4,
                        'justify-items-start': getFields.length >= 4,
                        'justify-items-center': getFields.length < 4,
                    }"
                >
                    <div
                        v-for="(field, index) in getFields"
                        :key="`field-${index}`"
                        class="flex flex-col gap-1"
                    >
                        <p
                            class="text-sm font-medium text-gray-500 whitespace-nowrap"
                        >
                            {{ field.label }}
                        </p>
                        <p class="text-base text-gray-900">
                            {{ field.value }}
                        </p>
                    </div>
                </div>
            </div>
        </template>
    </BaseTitledCard>
</template>
